import { Box, List, styled } from '@mui/material';
import { PolicyEvaluationContext, TimeWindow } from '@provision/sidekick-utils';
import React, { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { PolicyCard } from './policy-card';
import { sortPoliciesForDisplay } from './policy-order';

interface SupportPolicyListProps {
  partnerId: number;
  isLoading: boolean;
  filteredPolicies: NormalizedSupportPolicy[];
  expanded: string[];
  handleExpandClick: (id: string) => void;
  hasAccessToManageSupportPolicies: boolean;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  policyEvaluationContext: PolicyEvaluationContext;
  evaluationServiceLevel: string;
  isPreLits?: boolean;
  getEscalationCutoffHoursForUrgency?: (urgencyLevel: string) => TimeWindow[];
  focusPolicyId?: string | null;
  onFocusPolicyHandled?: () => void;
}

const LoadingPlaceholder = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(1.5),
  backgroundColor: theme.palette.action.hover,
  height: 60,
}));

export const SupportPolicyList = ({
  partnerId = PLATFORM_PARTNER_ID,
  isLoading,
  filteredPolicies,
  expanded,
  handleExpandClick,
  hasAccessToManageSupportPolicies,
  handleMenuOpen,
  policyEvaluationContext,
  evaluationServiceLevel,
  isPreLits,
  getEscalationCutoffHoursForUrgency,
  focusPolicyId,
  onFocusPolicyHandled,
}: SupportPolicyListProps): React.ReactElement => {
  const stableCurrentTime = useMemo(
    () => policyEvaluationContext.currentTime ?? new Date(),
    [policyEvaluationContext.currentTime],
  );

  const sortedPolicies = useMemo(
    () => sortPoliciesForDisplay(filteredPolicies),
    [filteredPolicies],
  );

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <>
        <LoadingPlaceholder />
        <LoadingPlaceholder />
        <LoadingPlaceholder />
      </>
    );
  } else if (sortedPolicies.length === 0) {
    content = (
      <Box
        data-testid="support-policy-list-empty"
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
          fontSize: '0.9rem',
        }}
      >
        No policies found
      </Box>
    );
  } else {
    content = sortedPolicies.map((policy) => (
      <PolicyCard
        key={policy.id}
        policy={policy}
        partnerId={partnerId}
        expanded={expanded.includes(policy.id)}
        hasAccessToManageSupportPolicies={hasAccessToManageSupportPolicies}
        onExpandClick={() => handleExpandClick(policy.id)}
        onMenuOpen={(e) => handleMenuOpen(e, policy.id)}
        evaluationServiceLevel={evaluationServiceLevel}
        currentTime={stableCurrentTime}
        policyEvaluationContext={policyEvaluationContext}
        isPreLits={isPreLits}
        getEscalationCutoffHoursForUrgency={getEscalationCutoffHoursForUrgency}
        isFocused={focusPolicyId === policy.id}
        onFocusHandled={onFocusPolicyHandled}
      />
    ));
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }} data-testid="support-policy-list">
      <List disablePadding>{content}</List>
    </Box>
  );
};
